import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>
      
      <div className="prose prose-blue max-w-none">
        <p className="text-sm text-gray-500 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
          <p className="text-gray-700 mb-4">
            By accessing or using D-ESPIAL Security Platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
            If you disagree with any part of these terms, you may not access the Service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
          <p className="text-gray-700 mb-4">
            D-ESPIAL Security Platform provides security intelligence, threat analysis, and risk assessment services 
            for individuals and organizations operating worldwide. Our services include security reports, 
            threat monitoring, and personalized security recommendations.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Subscription Terms</h2>
          <p className="text-gray-700 mb-4">
            Our Service is offered through subscription plans:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li><strong>Gold Plan:</strong> $30/month - Basic security intelligence features</li>
            <li><strong>Platinum Plan:</strong> $50/month - Enhanced features and priority support</li>
            <li><strong>Diamond Plan:</strong> $500/month - Enterprise-level access with dedicated support</li>
          </ul>
          <p className="text-gray-700 mb-4">
            Subscription fees are billed in advance on a monthly basis. Access to features is granted according to your selected plan tier.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Auto-Renewal Disclosure</h2>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
            <p className="text-gray-700">
              <strong>Important:</strong> Your subscription will automatically renew at the end of each billing period 
              unless you cancel before the renewal date. You will be charged the then-current subscription rate for your plan.
              You can cancel your subscription at any time through your account settings or by contacting support.
            </p>
          </div>
          <p className="text-gray-700 mb-4">
            We will notify you via email before any price changes take effect, giving you the opportunity to cancel 
            before the new rates apply.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Cancellation Policy</h2>
          <p className="text-gray-700 mb-4">
            You may cancel your subscription at any time. Upon cancellation:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li>Your subscription will remain active until the end of your current billing period</li>
            <li>You will retain access to all features of your plan until the subscription expires</li>
            <li>No partial refunds will be issued for unused time in the current billing period</li>
            <li>Your account data will be retained for 30 days after subscription expiration, after which it may be deleted</li>
          </ul>
          <p className="text-gray-700 mb-4">
            To cancel your subscription, visit your <Link to="/profile" className="text-blue-600 hover:text-blue-500">account settings</Link> or 
            contact our <Link to="/contact" className="text-blue-600 hover:text-blue-500">support team</Link>.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Refund Policy</h2>
          <p className="text-gray-700 mb-4">
            We offer refunds under the following circumstances:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li><strong>First-time subscribers:</strong> Full refund within 7 days of initial subscription if you're not satisfied with the service</li>
            <li><strong>Service outages:</strong> Pro-rated refund for extended service outages (more than 24 consecutive hours) that affect your ability to use the platform</li>
            <li><strong>Billing errors:</strong> Full refund for any duplicate charges or billing errors</li>
          </ul>
          <p className="text-gray-700 mb-4">
            Refund requests must be submitted within 30 days of the charge date. To request a refund, 
            please contact our <Link to="/contact" className="text-blue-600 hover:text-blue-500">support team</Link> with 
            your account email and reason for the refund request.
          </p>
          <p className="text-gray-700 mb-4">
            Refunds are processed within 5-10 business days and will be credited to the original payment method.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">7. User Responsibilities</h2>
          <p className="text-gray-700 mb-4">
            As a user of our Service, you agree to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Not share your account access with unauthorized parties</li>
            <li>Use the Service only for lawful purposes</li>
            <li>Not attempt to circumvent any security measures</li>
            <li>Not redistribute or resell security reports without authorization</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Intellectual Property</h2>
          <p className="text-gray-700 mb-4">
            All content, reports, analysis, and materials provided through the Service are the intellectual property 
            of D-ESPIAL and are protected by copyright and other intellectual property laws. You may use the content 
            for your personal or internal business purposes only.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Disclaimer of Warranties</h2>
          <p className="text-gray-700 mb-4">
            The Service is provided "as is" without warranties of any kind. While we strive to provide accurate and 
            up-to-date security intelligence, we cannot guarantee the completeness or accuracy of all information. 
            Security assessments and recommendations should be used as one input in your overall security planning.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
          <p className="text-gray-700 mb-4">
            D-ESPIAL shall not be liable for any indirect, incidental, special, consequential, or punitive damages 
            resulting from your use of or inability to use the Service. Our total liability shall not exceed the 
            amount you paid for the Service in the twelve months preceding the claim.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
          <p className="text-gray-700 mb-4">
            We reserve the right to modify these Terms at any time. We will notify users of material changes via email 
            or through the Service. Continued use of the Service after changes constitutes acceptance of the modified Terms.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
          <p className="text-gray-700 mb-4">
            For questions about these Terms, please contact us:
          </p>
          <ul className="list-none text-gray-700 mb-4 space-y-1">
            <li>Email: support@d-espial.com</li>
            <li>Visit our <Link to="/contact" className="text-blue-600 hover:text-blue-500">Contact Page</Link></li>
          </ul>
        </section>

        <div className="border-t border-gray-200 pt-8 mt-8">
          <p className="text-sm text-gray-500">
            By using our Service, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
          <div className="mt-4 flex space-x-4">
            <Link to="/privacy" className="text-blue-600 hover:text-blue-500 text-sm">Privacy Policy</Link>
            <Link to="/contact" className="text-blue-600 hover:text-blue-500 text-sm">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
